/**
 * A tab row that scrolls sideways when it does not fit, with the overflow cue from the
 * Administration tabs (0.91.0): a fade and an arrow on each side that has tabs out of view.
 * The arrows are pointer shortcuts only (aria-hidden, not focusable): the tabs themselves stay
 * reachable with the keyboard. The current tab is kept in view when it changes.
 * Shared by Administration, Help, Workflows, the asset details pane and the editor's panes (1.0.0-rc.9).
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { overflowSides, revealDelta, scrollStep } from './tabOverflow';

/** Room kept beside a revealed tab so the fade and arrow on that side do not cover it. */
const REVEAL_PAD = 30;

export function useTabOverflow<T extends HTMLElement>(activeKey: unknown) {
  const ref = useRef<T>(null);
  const [more, setMore] = useState({ left: false, right: false });
  const measure = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const next = overflowSides(el.scrollLeft, el.clientWidth, el.scrollWidth);
    setMore((m) => (m.left === next.left && m.right === next.right ? m : next));
  }, []);
  // The current tab (aria-selected or a NavLink's .active) scrolls into view; only this row scrolls, never the page.
  useLayoutEffect(() => {
    const el = ref.current;
    const active = el?.querySelector<HTMLElement>('[aria-selected="true"], .active');
    if (el && active) {
      const delta = revealDelta(el.getBoundingClientRect(), active.getBoundingClientRect(), REVEAL_PAD);
      if (delta) el.scrollLeft += delta;
    }
    measure();
  }, [activeKey, measure]);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    measure();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    ro?.observe(el);
    // The tabs inside change width too (permissions, counts, badges, fonts loading).
    for (const child of el.children) ro?.observe(child);
    const mo = typeof MutationObserver !== 'undefined' ? new MutationObserver(() => {
      for (const child of el.children) ro?.observe(child);
      measure();
    }) : null;
    mo?.observe(el, { childList: true });
    window.addEventListener('resize', measure);
    return () => { ro?.disconnect(); mo?.disconnect(); window.removeEventListener('resize', measure); };
  }, [measure]);
  const scroll = useCallback((dir: -1 | 1) => {
    const el = ref.current;
    if (el) el.scrollBy({ left: scrollStep(el.clientWidth) * dir, behavior: 'smooth' });
  }, []);
  return { ref, more, measure, scroll };
}

interface OverflowTabsProps {
  /** Changes when the current tab changes, so it is scrolled into view. */
  activeKey: unknown;
  children: ReactNode;
  /** Class of the scrolling row (admin-tabs for page tab rows, tab-strip for pane tab rows). */
  className: string;
  /** Class of the wrapper that holds the row, the fades and the arrows. */
  wrapClassName: string;
  /** nav for page sections (links), div with role tablist for a pane's tabs. */
  as?: 'nav' | 'div';
  role?: string;
  label?: string;
  /** Test ids of the arrows: `${testId}-left` and `${testId}-right`. */
  testId: string;
}

export function OverflowTabs({ activeKey, children, className, wrapClassName, as = 'div', role, label, testId }: OverflowTabsProps) {
  const { ref, more, measure, scroll } = useTabOverflow<HTMLDivElement>(activeKey);
  const Row = as;
  return (
    <div className={`${wrapClassName} tabs-overflow ${more.left ? 'more-left' : ''} ${more.right ? 'more-right' : ''}`}>
      <Row className={className} role={role} aria-label={label} ref={ref} onScroll={measure} data-testid={`${testId}-row`}>
        {children}
      </Row>
      {more.left && <button type="button" className="tabs-more left" aria-hidden tabIndex={-1} onClick={() => scroll(-1)} data-testid={`${testId}-left`}><ChevronLeft size={16} /></button>}
      {more.right && <button type="button" className="tabs-more right" aria-hidden tabIndex={-1} onClick={() => scroll(1)} data-testid={`${testId}-right`}><ChevronRight size={16} /></button>}
    </div>
  );
}
